import React, { VFC, useRef, useState, useEffect } from 'react';
import { NeueEditorContent } from './neue-editor-content';
import { useEditorStore } from './editor-store';
import { FontPreloader } from './font-preloader';
import { FeatureFlagsPanel } from './feature-flags-panel';
import { animated, SpringConfig, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import { ShareSlideover } from 'src/common/components/share/slide-over';
import { useEditorContext } from 'src/store/editor';
import { MemoizedSectionBar } from './section-bar';
import { useCurrentOrganization } from 'src/store/organization';
import { CmdkCommands } from 'src/cmdk/commands';
import { CmdkTextCommands } from 'src/cmdk/text-commands';
import { isBlockUploading } from './helpers/block/is-block-uploading';
import { useConditionalBeforeUnloadListener } from './utils/conditional-before-unload-listener.hook';
import { useMount, useUpdateEffect } from 'react-use';
import { useApplyThemeAtRoot } from './helpers/themes/apply-theme-at-root.hook';
import { TicTacToe } from './tic-tac-toe';
import { shallow } from 'zustand/shallow';
import { useLogoStore } from './logo/store';
import { PlayerPreview } from './editor/player-preview';
import { NeueLoadingSpinner } from './components/loading/spinner';
import { SafariRootSelectionClassFixer } from './components/safari-root-selection-class-fixer';
import { RecordingControls } from './recording/controls';
import { MemoizedBlockControlPanelContainer } from './block-control-panel-container';
import { EditorTopBar } from './editor-top-bar';
import { ContentCreateBar } from './create-toolbar';
import { isReadonly } from './native-e-signature/utils';
import { functionNoop } from 'src/utils/function/noop';
import { useEditorDragActions } from './utils/editor-drag-actions.hook';
import { useBlockIdActions } from './helpers/actions/use-block-id-actions.hook';
import { ReelExpandButton } from './components/reel-expand-button';
import { useEditorKeydownHook } from './utils/editor-keydown-hook';
import { usePasteFromClipboard } from './utils/paste-from-clipboard.hook';
import { useCreateBlockActions } from './utils/create-block-actions.hook';
import { MultipleFolders } from './section-card-folder';

// import JoyRideOnboardingGuide from 'src/onboarding/joyride-onboarding';

export interface ContentCreationEditorProps {}

const PREVIEW_OPEN_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

const MemoizedNeueEditorContent = React.memo(NeueEditorContent);

export const ContentCreationEditor: VFC<ContentCreationEditorProps> = () => {
  const initialized = useEditorStore((state) => state.initialized);
  const layoutStage = useEditorStore((state) => state.layoutStage);
  const sections = useEditorStore((state) => state.layout.sections);
  const themeSettings = useEditorStore((state) => state.themeSettings);
  const rootRenderMode = useEditorStore((state) => state.rootRenderMode);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);
  // const selectBlock = useEditorStore((state) => state.selectBlock);
  const setSectionDrawerOpen = useEditorStore((state) => state.setSectionDrawerOpen);
  const [domLoaded, setDomLoaded] = useState(false);

  const readonly = isReadonly(sections);
  const { setOrganisationLogos, setRecipientLogos, orgLogosFetched } = useLogoStore((state) => state, shallow);

  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const setJourney = useEditorContext((state) => state.setJourney);
  const journey = useEditorContext((state) => state.journey);

  const panelBarRef = useRef<HTMLDivElement>();

  const cmdkEnabled = currentOrganization?.feature_flags?.cmdk_enabled || process.env.ENVIRONMENT === 'development';
  const { is_folders_enabled: foldersEnabled } = journey;

  const { onDrag: onDragFiles, onDrop: onDropFiles, resetBoundaries } = useEditorDragActions();
  const { onNewFileByBlockId } = useBlockIdActions();

  useEditorKeydownHook();

  const { createBlock } = useCreateBlockActions();
  usePasteFromClipboard(createBlock);

  useMount(() => {
    if (currentOrganization) {
      setOrganisationLogos(currentOrganization);
    }
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDomLoaded(true);
    }, 3000);

    return () => {
      clearTimeout(handler);
    };
  }, []);

  useUpdateEffect(() => {
    if (orgLogosFetched) {
      setRecipientLogos(journey);
    }
  }, [orgLogosFetched, journey]);

  const isAnyBlockUploading = sections.flatMap((section) => section.blocks.filter(isBlockUploading)).length > 0;
  useConditionalBeforeUnloadListener(isAnyBlockUploading);

  useApplyThemeAtRoot(themeSettings);

  const editorSprings = useSpring({
    opacity: rootRenderMode === 'editor' && initialized ? 1 : 0,
    config: PREVIEW_OPEN_ANIMATION_CONFIG,
    immediate: !initialized,
  });

  const [previewEnterAnimationComplete, setPreviewEnterAnimationComplete] = React.useState(false);
  const previewSprings = useSpring({
    opacity: rootRenderMode === 'player' ? 1 : 0,
    config: PREVIEW_OPEN_ANIMATION_CONFIG,
    onStart: () => {
      setPreviewEnterAnimationComplete(false);
    },
    onRest: () => {
      if (rootRenderMode === 'player') {
        setPreviewEnterAnimationComplete(true);
      }
    },
  });

  const onDragOverContainer = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onDragFiles(e);
  };

  const onDropOverContainer = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const { files } = e.dataTransfer;
    for (const file of files) {
      const generatedBlockId = onDropFiles(e, {
        type: 'placeholder',
        placeholderType: 'add-file',
      });
      if (generatedBlockId) {
        onNewFileByBlockId(generatedBlockId)(file);
      }
    }
    resetBoundaries();
  };

  return (
    <div
      className='h-screen w-full transition-colors bg-neue-canvas-bg'
      onDrop={onDropOverContainer}
      onDragOver={readonly ? functionNoop : onDragOverContainer}
      onDragLeave={readonly ? functionNoop : resetBoundaries}
    >
      {/* {domLoaded && <JoyRideOnboardingGuide initial={false} />} */}

      <animated.div
        className={classNames('relative h-screen w-full transition-colors bg-neue-canvas-bg overflow-hidden', {
          // 'selection:bg-none selection:text-none': isDraggingSomethingOnEditor,
        })}
        style={editorSprings}
      >
        <MemoizedNeueEditorContent />
        <EditorTopBar onClickContainer={() => readonly && functionNoop} enableCmdk={cmdkEnabled} />
        <div className='absolute z-neue-section-bar left-0 top-[84px] bottom-[92px] flex items-center w-[300px] w-full'>
          {foldersEnabled ? <MultipleFolders vertical scrollable={true} /> : <MemoizedSectionBar />}
        </div>
        <div className='absolute z-neue-section-bar left-6 bottom-6 pointer-events-none flex items-center'>
          <ReelExpandButton onClick={() => setSectionDrawerOpen((open) => !open)} />
        </div>
        <div
          className='absolute z-neue-new-block-button-bar top-0 h-full right-6 flex justify-center items-center pointer-events-none'
          onClick={(e) => {
            if (!panelBarRef.current?.contains(e.target as HTMLDivElement)) {
              clearSelectedBlock();
            }
          }}
        >
          <div
            className='relative pointer-events-none'
            ref={(el: HTMLDivElement) => {
              panelBarRef.current = el;
            }}
          >
            <MemoizedBlockControlPanelContainer />
            <ContentCreateBar />
          </div>
        </div>
        <FeatureFlagsPanel />
        <RecordingControls />
        <ShareSlideover onUpdateJourney={setJourney} />
        <FontPreloader />
        {cmdkEnabled && <CmdkCommands />}
        <CmdkTextCommands />
        <TicTacToe />
        <SafariRootSelectionClassFixer />
      </animated.div>
      {rootRenderMode === 'editor' && layoutStage !== 'ready' && (
        <div className={classNames('absolute inset-0 flex items-center justify-center')}>
          <NeueLoadingSpinner size='32' theme='canvas' />
        </div>
      )}
      <PlayerPreview
        opacity={previewSprings.opacity}
        show={rootRenderMode === 'player'}
        readyToRender={previewEnterAnimationComplete}
        journey={journey}
      />
    </div>
  );
};
